import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';

import { NotificationService } from '@shared/services';
import { ImageConversionService } from '@shared/services/image-conversion/image-conversion.service';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: true,
  imports: [RktIconComponent],
})
export class FileUploadComponent {
  private notificationService = inject(NotificationService);

  private imageConversion = inject(ImageConversionService);

  @ViewChild('fileSelector') public fileSelector!: ElementRef;

  @Input() fileTypes = ['text/csv'];

  @Input() uploadedFileLabel?: string;

  @Input() title = 'Drag & drop file here';

  isDragging = false;

  fileName?: string;

  @Output() public selectedFile = new EventEmitter<File | null>();

  get fileFormats(): string {
    const formats = this.fileTypes.map((item: string) => {
      // Accepts both MIME types (`text/csv`) and extensions (`.csv`).
      const extension = item.split(/[./]/u).pop()?.toUpperCase();

      if (item.includes('jpeg')) {
        return 'JPEG';
      } else if (item.includes('png')) {
        return 'PNG';
      } else if (item.includes('pdf')) {
        return 'PDF';
      } else if (item.includes('jpg')) {
        return 'JPG';
      } else {
        return extension;
      }
    });

    return formats.join(', ');
  }

  public onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    this.isDragging = false;
    return this.handleSelectedFile(event.dataTransfer?.files[0]);
  }

  public onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    return this.handleSelectedFile(input.files?.[0], () => {
      input.value = '';
    });
  }

  // HEIC images (often mislabeled .jpg) don't render outside Safari once stored, so transcode them
  // to JPEG here before the type/size checks — the checks then see a genuinely web-safe file.
  private async handleSelectedFile(file: File | undefined, onAccept?: () => void): Promise<void> {
    if (!file) {
      this.showErrorFormatModal();
      return;
    }

    const prepared = this.fileTypes.includes('image/jpeg') ? await this.imageConversion.toUploadableFile(file) : file;

    if (this.fileTypes.includes(prepared.type)) {
      if (prepared.size === 0) {
        this.showErrorModal('The selected file is empty. Please choose another file.');
        return;
      }

      if (this.isFileExceededSize(prepared)) {
        this.showErrorSizeModal();
        return;
      }

      this.setFileName(prepared);
      onAccept?.();
      this.fileEmit(prepared);
    } else {
      this.showErrorFormatModal();
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  public stopDrag(event: DragEvent): void {
    this.isDragging = false;
    event.preventDefault();
    event.stopPropagation();
  }

  setFileName(file: File | null): void {
    this.fileName = file?.name ?? undefined;
  }

  public fileEmit(file: File | null): void {
    this.selectedFile.emit(file);
  }

  onDeleteFileBtnClick(): void {
    this.setFileName(null);
    this.fileEmit(null);
  }

  onReplaceBtnClick(): void {
    this.setFileName(null);
    this.fileEmit(null);
    setTimeout(() => this.fileSelector?.nativeElement.click(), 0);
  }

  isFileExceededSize(file: File | null): boolean {
    const fileLimit = 1024 * 1024 * 5;
    return !!file && file.size > fileLimit;
  }

  showErrorFormatModal() {
    this.showErrorModal(`Incorrect format. Please select file of one of the types ${this.fileFormats} and re-upload.`);
  }

  showErrorSizeModal() {
    this.showErrorModal('The uploaded file cannot exceed 5MB in size.');
  }

  showErrorModal(errorText: string) {
    this.notificationService.displayError(errorText, 'Error');
  }
}
